import { IDatasetTableDefaultProps } from "components/dashboard/dataset-table/utils/interfaces";
import { WIDTH_LOOKUP_TABLE } from "components/dashboard/dataset-table/utils/utils";
import { I, T } from "services/lang/LanguageService";
import React, { useState } from "react";
import { TitleOverrideAndDescriptionTemplate } from "components/templates/DataTableCellTemplates";
import { Tooltip } from "react-tooltip";
import { ChronoUnitLegacy, dateToReadableString } from "utils/time-utils";
import ColumnTabWithMapper from "./ColumnTabWithMapper";
import { formatNumberWithGaps } from "../product-template-table/utils/utils";
import DatasetChartButtonWrapper, { TableTypes } from "./DatasetChartButtonWrapper";

const DatasetTableDefault = ({
  config,
  dateFrom,
  dateTo,
  data,
  datasetsArray,
  getCellStyle,
  totals,
  setIsDialogOpen,
  formatNumbers,
}: IDatasetTableDefaultProps) => {
  const showTotal = datasetsArray.some(obj => obj.showTotal);
  const [currentIdentifiers, setCurrentIdentifiers] = useState<string[]>([]);

  const ColumnHeader = () => (
    <tr>
      {!config?.hideDateColumn && (
        <th
          style={{
            borderRight: "1px solid #d1d1d1",
            padding: "2px 8px",
            width: WIDTH_LOOKUP_TABLE[config?.dateFormat || ChronoUnitLegacy.MINUTES],
          }}
        >
          {T("word.date")}
        </th>
      )}
      {datasetsArray?.map((col, i) => (
        <th key={i} style={{ padding: "2px 8px", borderLeft: "1px solid #d1d1d1" }}>
          {TitleOverrideAndDescriptionTemplate(
            {
              description: col.description,
              title: col.title,
              overriddenTitle: col.overriddenTitle,
              unit: col.unit,
              identifier: col.identifier,
            },
            2
          )}
          <DatasetChartButtonWrapper
            identifier={col.identifier}
            currentIdentifiers={currentIdentifiers}
            setCurrentIdentifiers={setCurrentIdentifiers}
            tableType={TableTypes.DEFAULT}
          />
        </th>
      ))}
    </tr>
  );

  const ColumnFooter = () => (
    <tr>
      <th scope="row" />
      {totals.length > 0 ? (
        totals.map(obj => (
          <td className="value-class" key={obj.identifier}>
            {obj.value ? (
              <>
                <Tooltip id="value-tooltip" opacity={0.7} place="top" float />
                <span data-tooltip-id="value-tooltip" data-tooltip-content={`${I(obj.tooltip)}`}>
                  {obj.value}
                </span>
              </>
            ) : null}
          </td>
        ))
      ) : (
        <td className="value-class" />
      )}
    </tr>
  );

  const dateBody = (date: string): string => {
    const dateFormat = config?.dateFormat || "MINUTES";
    return dateToReadableString(new Date(date), dateFormat as ChronoUnitLegacy);
  };

  const renderCellValue = (key, value, index) => {
    const columnConfig = datasetsArray[index];
    const decimalPlaces = columnConfig.decimalPlaces ?? 2;

    if (columnConfig.stringValueMapper) {
      return <ColumnTabWithMapper value={value} mapperId={columnConfig.stringValueMapper} />;
    }

    if (!value && value !== 0) {
      return "-";
    }

    if (typeof value === "number") {
      const formattedValue = formatNumbers
        ? formatNumberWithGaps(value.toFixed(decimalPlaces))
        : value.toFixed(decimalPlaces);
      return formattedValue;
    }

    return value;
  };

  const columnBody = data.map((obj, index) => {
    if (config?.transposed) {
      return <tr key={index} />;
    }
    return (
      <tr key={index}>
        {!config?.hideDateColumn && (
          <td style={{ whiteSpace: "nowrap", padding: "2px 8px" }}>{dateBody(obj.date)}</td>
        )}
        {Object.entries(obj).map(([key, value], idx) => {
          if (key !== "date") {
            return (
              <td key={idx} className="value-class" style={getCellStyle(key, value)}>
                {renderCellValue(key, value, idx)}
              </td>
            );
          }
          return null;
        })}
      </tr>
    );
  });

  return (
    <div className="data-table-wrapper default-table">
      <table className="min-table-width">
        <thead>
          <ColumnHeader />
        </thead>
        <tbody>{columnBody}</tbody>
        {showTotal && (
          <tfoot>
            <ColumnFooter />
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default DatasetTableDefault;
